/**
 * Chatbot.jsx — Ezy, EsyAI's site assistant
 *
 * What changed from the old version:
 *  - All LLM calls go to /api/chat (server-side). No provider key in the client.
 *  - Old OpenRouter key + SYSTEM_PROMPT removed entirely.
 *  - Old localhost:8000 booking flow removed.
 *  - session state persists visitor's name/email/company/need across turns.
 *  - Rule-based LeadCard trigger (not keyword-only, not model-decided).
 *  - Hard stop at 20 turns; widget hidden on /privacy and /terms.
 *  - dangerouslySetInnerHTML replaced with a safe renderer.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageCircle, X, Send, Loader2, Bot, User, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import LeadCard from './chat/LeadCard';

// ─── Constants ────────────────────────────────────────────────────────────────

const WELCOME_MESSAGE = {
  role: 'assistant',
  content:
    "Hi! I'm **Ezy**, EsyAI's assistant. 👋\n\nI can answer questions about our services — websites, automation, custom apps, AI agents, and more. What can I help you with today?",
};

const QUICK_REPLIES = [
  'What services do you offer?',
  'How long does a website take?',
  'How much does it cost?',
  'I want to get a quote',
];

// Routes where the chatbot bubble is hidden
const HIDDEN_ROUTES = ['/privacy', '/terms'];

// After this many turns, stop calling the model and prompt the lead card
const MAX_TURNS = 20;

// How many turns before we soft-prompt for the lead card
const SOFT_PROMPT_AFTER = 4;

// Keywords that trigger "show the need has been described" session capture
const NEED_SIGNALS = [
  /\bi\s+(need|want|have|am\s+looking\s+for|would\s+like)\b/i,
  /\bmy\s+(business|company|clinic|shop|store|client|team)\b/i,
  /\bwe\s+(need|want|are\s+looking\s+for|would\s+like)\b/i,
  /\bhelp\s+(me|us)\s+with\b/i,
  /\bautomat\b/i,
  /\bwebsite\b.*\bfor\b/i,
  /\bapp\s+for\b/i,
];

// Keywords that should trigger showing the LeadCard immediately
const LEAD_CARD_TRIGGERS = [
  /\bbook\b/i,
  /\bschedule\b/i,
  /\bquote\b/i,
  /\bget\s+in\s+touch\b/i,
  /\bcontact\b/i,
  /\bspeak\s+to\s+(the\s+)?team\b/i,
  /\btalk\s+to\s+(someone|a\s+human)\b/i,
  /\bprice\b/i,
  /\bhow\s+much\b/i,
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Safe inline formatter: only bold (**text**) and newlines. No raw HTML from the model.
 */
function SafeText({ content }) {
  if (!content) return null;
  // Split on **...** markers
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  const lines = [];

  parts.forEach((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      lines.push(<strong key={i}>{part.slice(2, -2)}</strong>);
    } else {
      // Split on newlines and insert <br>
      const segments = part.split('\n');
      segments.forEach((seg, j) => {
        lines.push(<span key={`${i}-${j}`}>{seg}</span>);
        if (j < segments.length - 1) lines.push(<br key={`br-${i}-${j}`} />);
      });
    }
  });

  return <>{lines}</>;
}

function mayTriggerLeadCard(text) {
  return LEAD_CARD_TRIGGERS.some((r) => r.test(text));
}

const DEVICE_ID_KEY = 'esyai_device_id';

/**
 * Stable per-browser ID so the server can rate-limit per visitor instead of
 * per IP — otherwise everyone on the same WiFi/office network shares one quota.
 * Falls back to null if storage is blocked (private mode, etc.); the server
 * then falls back to IP-based limiting for that request.
 */
function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function extractNeed(text) {
  return NEED_SIGNALS.some((r) => r.test(text)) ? text.trim().slice(0, 800) : null;
}

// Reliable, low-false-positive: a bare email address anywhere in the message.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/;

function extractEmail(text) {
  const m = text.match(EMAIL_RE);
  return m ? m[0] : null;
}

// Words that commonly follow "I'm" / "I am" but aren't names — filters out
// the biggest source of false positives for the loose name patterns below.
const NAME_STOPWORDS = new Set([
  'looking', 'interested', 'trying', 'working', 'building', 'just', 'not',
  'also', 'still', 'here', 'ready', 'currently', 'planning', 'hoping',
  'wanting', 'going', 'thinking', 'considering', 'based', 'from', 'in',
  'on', 'at', 'a', 'an', 'the', 'sure', 'new', 'happy', 'excited', 'glad',
  'wondering', 'curious', 'open', 'keen', 'seeking', 'after', 'about',
  'with', 'reaching', 'writing', 'asking', 'checking', 'emailing',
]);

const NAME_PATTERNS = [
  /\bmy name(?:'s| is)\s+([a-zA-Z][a-zA-Z'-]*(?:\s+[a-zA-Z][a-zA-Z'-]*){0,2})/i,
  /\bcall me\s+([a-zA-Z][a-zA-Z'-]*(?:\s+[a-zA-Z][a-zA-Z'-]*){0,2})/i,
  /\b(?:i'?m|i am)\s+([a-zA-Z][a-zA-Z'-]*(?:\s+[a-zA-Z][a-zA-Z'-]*){0,2})(?=[\s,.!]|$)/i,
];

/**
 * Best-effort first-name-or-more extraction from free text. Only ever used
 * to pre-fill the LeadCard form — a wrong guess is just an editable field
 * the visitor sees before they click send, never auto-submitted.
 */
function extractName(text) {
  for (const re of NAME_PATTERNS) {
    const m = text.match(re);
    if (!m || !m[1]) continue;
    const candidate = m[1].trim();
    if (candidate.length > 40) continue;
    const firstWord = candidate.split(/\s+/)[0].toLowerCase();
    if (NAME_STOPWORDS.has(firstWord)) continue;
    return candidate.replace(/\b\w/g, (c) => c.toUpperCase());
  }
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Chatbot() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showQuick, setShowQuick] = useState(true);

  // Visitor context — persisted across turns, sent to /api/chat each call
  const [session, setSession] = useState({ name: '', email: '', company: '', need: '' });

  // LeadCard state: 'hidden' | 'visible' | 'done'
  const [leadCard, setLeadCard] = useState('hidden');

  // Turn counter (user messages sent to the model)
  const [turnCount, setTurnCount] = useState(0);

  // Whether we have already soft-prompted for the lead card this session
  const softPromptedRef = useRef(false);

  // Whether we've already auto-surfaced the lead card once name+email+need are all known
  const leadReadyPromptedRef = useRef(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // ── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, leadCard]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 300);
  }, [open]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const addMsg = useCallback((role, content) => {
    setMessages((prev) => [...prev, { role, content }]);
  }, []);

  const updateSession = useCallback((updates) => {
    setSession((prev) => ({ ...prev, ...updates }));
  }, []);

  // ── Core send function ────────────────────────────────────────────────────

  const send = useCallback(
    async (text) => {
      const msg = (text || input).trim();
      if (!msg || loading) return;

      setShowQuick(false);
      setInput('');

      // Hard stop
      if (turnCount >= MAX_TURNS) {
        addMsg('user', msg);
        addMsg(
          'assistant',
          "We've covered a lot of ground! 😊 To make sure nothing gets missed, please use the form below or visit our **Contact page** to leave your details and the team will be right with you."
        );
        setLeadCard('visible');
        return;
      }

      // Capture visitor facts mentioned in free text — name, email, need —
      // so the LeadCard below arrives pre-filled instead of asking again.
      const detectedNeed = extractNeed(msg);
      const detectedEmail = extractEmail(msg);
      const detectedName = extractName(msg);

      const sessionUpdates = {};
      if (detectedNeed && !session.need) sessionUpdates.need = detectedNeed;
      if (detectedEmail && !session.email) sessionUpdates.email = detectedEmail;
      if (detectedName && !session.name) sessionUpdates.name = detectedName;
      if (Object.keys(sessionUpdates).length) updateSession(sessionUpdates);

      // What we know right now, including anything just captured above —
      // `session` itself won't reflect sessionUpdates until the next render.
      const effectiveName = sessionUpdates.name || session.name;
      const effectiveEmail = sessionUpdates.email || session.email;
      const effectiveNeed = sessionUpdates.need || session.need;

      // Rule-based lead card trigger
      if (mayTriggerLeadCard(msg)) {
        addMsg('user', msg);
        addMsg(
          'assistant',
          "Great — I can pass your details straight to the team. Fill in the quick form below and we'll be in touch within 24 hours. 👇"
        );
        setLeadCard('visible');
        return;
      }

      addMsg('user', msg);
      setLoading(true);

      // Build history to send (last 12 turns, no system messages)
      const historyToSend = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .slice(-12)
        .map((m) => ({ role: m.role, content: m.content }));

      try {
        const res = await fetch('/api/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: msg,
            messages: historyToSend,
            session,
            deviceId: getDeviceId(),
          }),
        });

        const data = await res.json();

        if (res.status === 429) {
          addMsg('assistant', data.reply || "You're sending messages a bit fast. Please wait a moment.");
          return;
        }

        if (!res.ok) {
          addMsg(
            'assistant',
            data.reply ||
              "I'm having a technical issue right now. Please try again or email us at **info@esyai.co.uk**."
          );
          return;
        }

        addMsg('assistant', data.reply || "Sorry, I didn't get a response. Please try again.");

        const newTurnCount = turnCount + 1;
        setTurnCount(newTurnCount);

        if (
          effectiveName &&
          effectiveEmail &&
          effectiveNeed &&
          !leadReadyPromptedRef.current &&
          leadCard === 'hidden'
        ) {
          // We already have everything needed for a complete lead — don't
          // make them repeat it into a form, just surface it pre-filled.
          leadReadyPromptedRef.current = true;
          softPromptedRef.current = true;
          setTimeout(() => {
            addMsg(
              'assistant',
              `Thanks, ${effectiveName.split(' ')[0]} — I've got your details. Just hit **Send** below and the team will be in touch within 24 hours. 👇`
            );
            setLeadCard('visible');
          }, 600);
        } else if (
          newTurnCount >= SOFT_PROMPT_AFTER &&
          effectiveNeed &&
          !softPromptedRef.current &&
          leadCard === 'hidden'
        ) {
          // Soft prompt: after SOFT_PROMPT_AFTER useful turns with a captured need, invite lead card
          softPromptedRef.current = true;
          setTimeout(() => {
            addMsg(
              'assistant',
              "It sounds like you've got a clear project in mind. Want me to pass your details to the EsyAI team? Just click **Get a quote** in the header, or type 'quote' anytime. 😊"
            );
          }, 600);
        }
      } catch (err) {
        console.error('[Chatbot] fetch error:', err);
        addMsg(
          'assistant',
          "I'm having trouble connecting right now. Please try again in a moment or email us at **info@esyai.co.uk**."
        );
      } finally {
        setLoading(false);
      }
    },
    [input, loading, messages, session, turnCount, leadCard, addMsg, updateSession]
  );

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Hide on legal pages
  if (HIDDEN_ROUTES.includes(location.pathname)) return null;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Panel ─────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.25, 1, 0.5, 1] }}
            className="fixed bottom-[calc(9.5rem+env(safe-area-inset-bottom,0px))] right-4 sm:right-6 z-[2147483000] w-[calc(100vw-2rem)] sm:w-[390px] max-h-[min(82vh,calc(100vh-12rem))] flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-white/10"
            style={{ background: 'rgba(10,10,10,0.95)', backdropFilter: 'blur(20px)' }}
            role="dialog"
            aria-label="EsyAI chat assistant"
          >
            {/* Header */}
            <div
              className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0"
              style={{ background: 'rgba(255,255,255,0.03)' }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}
              >
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium font-body">Ezy — EsyAI Assistant</p>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse shrink-0" />
                  <span className="text-white/40 text-xs font-body">Online · GPT-4o mini</span>
                </div>
              </div>

              {/* Get a quote button */}
              {leadCard === 'hidden' && (
                <button
                  id="chatbot-get-quote-btn"
                  onClick={() => {
                    addMsg(
                      'assistant',
                      "Sure! Fill in the quick form below and the team will be in touch within 24 hours. 👇"
                    );
                    setLeadCard('visible');
                  }}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border border-purple-500/40 text-purple-300 hover:bg-purple-500/15 transition-all shrink-0 font-body"
                >
                  <Sparkles className="w-3 h-3" />
                  Get a quote
                </button>
              )}

              <button
                id="chatbot-close-btn"
                onClick={() => setOpen(false)}
                className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors shrink-0"
                aria-label="Close chat"
              >
                <X className="w-4 h-4 text-white/60" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0">
              {messages.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-7 h-7 rounded-full shrink-0 flex items-center justify-center mt-0.5 ${
                      m.role === 'assistant'
                        ? 'bg-gradient-to-br from-purple-600 to-indigo-600'
                        : 'bg-white/10'
                    }`}
                  >
                    {m.role === 'assistant' ? (
                      <Bot className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <User className="w-3.5 h-3.5 text-white/70" />
                    )}
                  </div>
                  <div
                    className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm font-body font-light leading-relaxed ${
                      m.role === 'assistant'
                        ? 'text-white/90 border border-white/8'
                        : 'bg-white/10 text-white/90'
                    }`}
                    style={m.role === 'assistant' ? { background: 'rgba(124,58,237,0.15)' } : {}}
                  >
                    <SafeText content={m.content} />
                  </div>
                </motion.div>
              ))}

              {/* Typing indicator */}
              {loading && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex gap-2.5"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center shrink-0">
                    <Bot className="w-3.5 h-3.5 text-white" />
                  </div>
                  <div
                    className="rounded-2xl px-4 py-3 border border-white/8 flex gap-1 items-center"
                    style={{ background: 'rgba(124,58,237,0.15)' }}
                  >
                    {[0, 1, 2].map((idx) => (
                      <motion.span
                        key={idx}
                        className="w-1.5 h-1.5 rounded-full bg-white/50"
                        animate={{ y: [0, -4, 0] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: idx * 0.15 }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Quick replies — only on first message */}
              {showQuick && messages.length === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-wrap gap-2 pt-1"
                >
                  {QUICK_REPLIES.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      className="text-xs font-body px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:border-purple-500/50 hover:text-white hover:bg-purple-500/10 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </motion.div>
              )}

              {/* Lead Card */}
              {leadCard === 'visible' && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <LeadCard
                    session={session}
                    onSuccess={() => {
                      setLeadCard('done');
                      addMsg(
                        'assistant',
                        "✅ **Your details have been sent!** The team will email you within 24 hours. Is there anything else I can help with in the meantime?"
                      );
                    }}
                  />
                </motion.div>
              )}

              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div
              className="px-3 py-3 border-t border-white/10 shrink-0"
              style={{ background: 'rgba(255,255,255,0.02)' }}
            >
              {/* GDPR notice */}
              <p className="text-white/30 text-[10px] font-body text-center mb-2 leading-tight px-2">
                We use this chat to answer questions about EsyAI. Quote requests may be stored to contact you.{' '}
                <a href="/privacy" className="underline hover:text-white/50 transition-colors">
                  Privacy Policy
                </a>
              </p>

              <div
                className="flex items-center gap-2 rounded-xl border border-white/15 px-3 py-2 focus-within:border-purple-500/50 transition-colors"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <input
                  ref={inputRef}
                  id="chatbot-input"
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Ask me anything…"
                  className="flex-1 bg-transparent text-sm text-white font-body font-light placeholder:text-white/30 focus:outline-none"
                  aria-label="Chat message"
                  disabled={loading}
                />
                <button
                  id="chatbot-send-btn"
                  onClick={() => send()}
                  disabled={!input.trim() || loading}
                  aria-label="Send message"
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-all disabled:opacity-30 shrink-0"
                  style={{
                    background:
                      input.trim() && !loading
                        ? 'linear-gradient(135deg,#7c3aed,#4f46e5)'
                        : 'rgba(255,255,255,0.1)',
                  }}
                >
                  {loading ? (
                    <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 text-white" />
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Bubble ────────────────────────────────────────────────────────── */}
      <motion.button
        id="chatbot-bubble-btn"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 sm:right-6 z-[2147483000] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg,#7c3aed,#4f46e5)' }}
        aria-label={open ? 'Close chat' : 'Open chat'}
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div
              key="x"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="w-6 h-6 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="c"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <MessageCircle className="w-6 h-6 text-white" />
            </motion.div>
          )}
        </AnimatePresence>

        {!open && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-green-400 border-2 border-black flex items-center justify-center"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          </motion.span>
        )}
      </motion.button>
    </>
  );
}
